import {ChangeDetectionStrategy, Component, DestroyRef, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatButtonModule} from '@angular/material/button';
import {of} from 'rxjs';
import {finalize, map, switchMap, take} from 'rxjs/operators';
import {DialogTextModel} from '../../../core/cms/models/dialog.text.model';
import {AccountService} from '../../../core/account/services/account.rest.service';
import {DialogRestService} from '../../../core/cms/services/dialog.rest.service';
import {LogService} from '../../../core/base/log.service';

@Component({
  selector: 'app-e-invoice',
  standalone: true,
  imports: [ReactiveFormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatCheckboxModule, MatButtonModule],
  templateUrl: './e-invoice.component.html',
  styleUrl: './e-invoice.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EInvoiceComponent {
  readonly data = inject<DialogTextModel>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<EInvoiceComponent>);
  private readonly accountService = inject(AccountService);
  private readonly dialogRestService = inject(DialogRestService);
  private readonly log = inject(LogService);
  private readonly destroyRef = inject(DestroyRef);

  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal(false);
  readonly form = new FormGroup({
    eInvoiceEmail: new FormControl(this.data.eInvoiceEmail ?? '', {
      nonNullable: true,
      validators: [Validators.required, Validators.email]
    }),
    notify: new FormControl(false, {nonNullable: true})
  });

  constructor() {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.success.set(false);
    });
  }

  submit(): void {
    if (this.saving()) return;
    this.form.controls.eInvoiceEmail.setValue(this.form.controls.eInvoiceEmail.value.trim());
    this.form.markAllAsTouched();
    if (this.form.invalid) return;
    if (!this.data.idf) {
      this.error.set('Es wurde keine Apotheke ausgewählt. Bitte öffnen Sie das Fenster erneut.');
      this.log.error('Cannot save e-invoice settings without an IDF');
      return;
    }

    const {eInvoiceEmail, notify} = this.form.getRawValue();
    const disableClose = this.dialogRef.disableClose;
    this.saving.set(true);
    this.error.set(null);
    this.form.disable({emitEvent: false});
    this.dialogRef.disableClose = true;

    this.accountService.setEinvoiceEmail(this.data.idf, eInvoiceEmail, notify).pipe(
      take(1),
      switchMap(response => {
        if (response.error_code !== null) {
          throw new Error(`Unable to save e-invoice settings: ${response.error_code}`);
        }
        if (!this.data.subscription) return of(null);
        return this.dialogRestService.subscribeToDialogOffer(this.data.id, this.data.idf).pipe(
          take(1),
          map(subscription => {
            if (subscription.error_code !== null || subscription.return_object === null) {
              throw new Error('Unable to subscribe to e-invoice popup');
            }
            return null;
          })
        );
      }),
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        this.saving.set(false);
        this.form.enable({emitEvent: false});
        this.dialogRef.disableClose = disableClose;
      })
    ).subscribe({
      next: () => this.success.set(true),
      error: error => {
        this.error.set('Die Einstellungen konnten nicht vollständig gespeichert werden. Bitte versuchen Sie es erneut.');
        this.log.error('Unable to complete e-invoice popup', error);
      }
    });
  }
}
